import { useEffect, useRef, useState } from "react";
import api, { fileUrl, errText, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, Upload, X, FileDown, FileUp, Layers, Pencil, Check, Tag, Search, AlertTriangle } from "lucide-react";

const UNGROUPED = "Belum Dikelompokkan";

export default function Employees() {
  const { user } = useAuth();
  const canEdit = user?.is_super === true;
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("ALL");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", position: "", group: "", photo_url: "" });
  const [editUploading, setEditUploading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (e) => {
    setEditEmp(e);
    setEditForm({ name: e.name || "", position: e.position || "", group: e.group || "", photo_url: e.photo_url || "" });
  };

  const uploadEditPhoto = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setEditUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Foto berhasil diunggah");
    } catch (err) { toast.error(errText(err)); } finally { setEditUploading(false); }
  };

  const saveEdit = async (ev) => {
    ev.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/employees/${editEmp.employee_id}`, editForm);
      toast.success("Data karyawan diperbarui");
      setEditEmp(null);
      load();
    } catch (err) { toast.error(errText(err)); } finally { setEditSaving(false); }
  };
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", name: "", position: "", email: "", password: "", photo_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const bulkRef = useRef();

  const load = () => api.get("/employees").then((r) => setEmployees(r.data));
  const loadGroups = () => api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); loadGroups(); }, []);

  const toggleSelect = (eid) =>
    setSelected((s) => (s.includes(eid) ? s.filter((x) => x !== eid) : [...s, eid]));

  const applyAssign = async () => {
    if (selected.length === 0) { toast.error("Pilih minimal satu karyawan"); return; }
    setAssigning(true);
    try {
      const { data } = await api.put("/employees/groups", { employee_ids: selected, group: assignTo });
      toast.success(`${data.updated} karyawan diperbarui${assignTo ? ` → ${assignTo}` : " (dihapus dari kelompok)"}`);
      setSelected([]); setSelectMode(false); setAssignTo("");
      load();
    } catch (err) { toast.error(errText(err)); } finally { setAssigning(false); }
  };

  const addGroup = async () => {
    if (!newGroup.trim()) return;
    try { await api.post("/groups", { name: newGroup.trim() }); setNewGroup(""); loadGroups(); toast.success("Kelompok ditambahkan"); }
    catch (err) { toast.error(errText(err)); }
  };
  const renameGroup = async (g) => {
    const name = window.prompt("Ubah nama kelompok:", g.name);
    if (!name || name.trim() === g.name) return;
    try { await api.put(`/groups/${g.id}`, { name: name.trim() }); loadGroups(); load(); toast.success("Nama kelompok diperbarui"); }
    catch (err) { toast.error(errText(err)); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Hapus kelompok "${g.name}"? Karyawan di dalamnya akan menjadi Belum Dikelompokkan.`)) return;
    try { await api.delete(`/groups/${g.id}`); loadGroups(); load(); toast.success("Kelompok dihapus"); }
    catch (err) { toast.error(errText(err)); }
  };


  const downloadTemplate = async () => {
    try {
      await downloadFile("/employees/template", "template_data_karyawan.xlsx");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const bulkImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} karyawan ditambahkan, ${data.skipped} dilewati (sudah ada)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} baris bermasalah: ${data.errors[0]}`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setImporting(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Foto berhasil diunggah");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/employees", form);
      toast.success("Karyawan berhasil ditambahkan");
      setCreated(data.login);
      setForm({ employee_id: "", name: "", position: "", email: "", password: "", photo_url: "" });
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = (emp) => setDeleteTarget(emp);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/employees/${deleteTarget.employee_id}`);
      toast.success(`Karyawan "${deleteTarget.name}" dihapus permanen`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = employees.filter((e) => {
    const q = search.trim().toLowerCase();
    if (q && !(e.name.toLowerCase().includes(q) || String(e.employee_id).toLowerCase().includes(q))) return false;
    if (filterGroup === "ALL") return true;
    if (filterGroup === UNGROUPED) return !e.group;
    return e.group === filterGroup;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Data Karyawan</h1>
          <p className="mt-1 text-slate-500">Kelola profil dan akun karyawan peserta program.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="employee-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari nama atau No. ID…"
              className="w-56 rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-8 text-sm text-slate-700 outline-none focus:border-blue-500"
            />
            {search && (
              <button data-testid="employee-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <select data-testid="group-filter" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-blue-500">
            <option value="ALL">Semua Kelompok</option>
            <option value={UNGROUPED}>{UNGROUPED}</option>
            {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
          <button data-testid="download-employee-template-button" onClick={downloadTemplate} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition">
            <FileDown className="h-4 w-4" /> Unduh Template
          </button>
          {canEdit && (<>
          <button data-testid="manage-groups-button" onClick={() => setGroupModal(true)} className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm font-semibold text-[#F16522] hover:bg-orange-100 transition">
            <Layers className="h-4 w-4" /> Kelola Kelompok
          </button>
          <button data-testid="select-mode-button" onClick={() => { setSelectMode((v) => !v); setSelected([]); }} className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${selectMode ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            <Check className="h-4 w-4" /> {selectMode ? "Selesai Memilih" : "Pilih & Tetapkan Kelompok"}
          </button>
          <input ref={bulkRef} type="file" accept=".xlsx,.csv" onChange={bulkImport} className="hidden" data-testid="employee-import-input" />
          <button data-testid="import-employee-button" onClick={() => bulkRef.current?.click()} disabled={importing} className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 transition">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Impor Excel/CSV
          </button>
          <button data-testid="add-employee-button" onClick={() => { setOpen(true); setCreated(null); }} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
            <UserPlus className="h-4 w-4" /> Tambah Karyawan
          </button>
          </>)}
        </div>
      </div>

      {selectMode && canEdit && (
        <div data-testid="assign-toolbar" className="flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50/70 px-5 py-4">
          <span className="text-sm font-semibold text-slate-700" data-testid="selected-count">{selected.length} karyawan dipilih</span>
          <select data-testid="assign-group-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500">
            <option value="">— Hapus dari kelompok —</option>
            {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
          <button data-testid="apply-assign-button" onClick={applyAssign} disabled={assigning || selected.length === 0} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition">
            {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />} Tetapkan Kelompok
          </button>
          <button onClick={() => setSelected(filtered.map((e) => e.employee_id))} className="text-sm font-medium text-blue-700 hover:underline">Pilih semua</button>
          <button onClick={() => setSelected([])} className="text-sm font-medium text-slate-500 hover:underline">Kosongkan</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400" data-testid="employee-empty-state">{search.trim() ? `Tidak ada karyawan yang cocok dengan "${search.trim()}".` : "Tidak ada karyawan pada kelompok ini."}</div>
        )}
        {filtered.map((e) => {
          const isSel = selected.includes(e.employee_id);
          return (
          <div
            key={e.id}
            data-testid={`employee-card-${e.employee_id}`}
            onClick={() => { if (selectMode && canEdit) toggleSelect(e.employee_id); }}
            className={`rounded-2xl border bg-white p-5 shadow-sm transition ${selectMode && canEdit ? "cursor-pointer hover:border-blue-400" : "border-slate-200"} ${isSel ? "border-blue-600 ring-2 ring-blue-500/30" : ""}`}
          >
            <div className="flex items-center gap-4">
              {selectMode && canEdit && (
                <input data-testid={`select-employee-${e.employee_id}`} type="checkbox" checked={isSel} readOnly className="h-5 w-5 rounded border-slate-300 text-blue-600 accent-blue-600" />
              )}
              {e.photo_url ? (
                <img src={fileUrl(e.photo_url)} alt={e.name} className="h-14 w-14 rounded-xl object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-50 text-lg font-bold text-blue-600">{e.name[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{e.name}</p>
                <p className="text-sm text-slate-500 truncate">{e.position || "—"}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-600">{e.employee_id}</span>
                  <span data-testid={`employee-group-${e.employee_id}`} className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${e.group ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-400"}`}>{e.group || UNGROUPED}</span>
                </div>
              </div>
              {canEdit && !selectMode && (
                <div className="flex items-center gap-1" onClick={(ev) => ev.stopPropagation()}>
                  <button data-testid={`edit-employee-${e.employee_id}`} onClick={() => openEdit(e)} className="text-slate-400 hover:text-blue-600 transition" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`delete-employee-${e.employee_id}`} onClick={() => remove(e)} className="text-slate-400 hover:text-rose-600 transition" title="Hapus">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {editEmp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !editSaving && setEditEmp(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-employee-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-xl font-bold text-slate-900">Edit Data Karyawan</h3>
              <button onClick={() => !editSaving && setEditEmp(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <p className="mb-5 text-xs font-mono text-slate-400">No. ID: {editEmp.employee_id} (tidak dapat diubah)</p>
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="flex items-center gap-4">
                {editForm.photo_url ? (
                  <img src={fileUrl(editForm.photo_url)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-xl font-bold text-slate-400">{(editForm.name || "?")[0]}</div>
                )}
                <label className="cursor-pointer rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  {editUploading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Mengunggah…</span> : "Ganti Foto"}
                  <input data-testid="edit-photo-input" type="file" accept="image/*" className="hidden" onChange={uploadEditPhoto} disabled={editUploading} />
                </label>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nama</label>
                <input data-testid="edit-name-input" required value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Jabatan / Posisi</label>
                <input data-testid="edit-position-input" value={editForm.position} onChange={(e) => setEditForm((f) => ({ ...f, position: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Kelompok</label>
                <select data-testid="edit-group-select" value={editForm.group} onChange={(e) => setEditForm((f) => ({ ...f, group: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                  <option value="">Belum Dikelompokkan</option>
                  {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditEmp(null)} disabled={editSaving} className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Batal</button>
                <button data-testid="save-edit-employee" type="submit" disabled={editSaving || editUploading} className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  {editSaving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {groupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setGroupModal(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto" data-testid="group-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold text-slate-900">Kelola Kelompok</h3>
              <button onClick={() => setGroupModal(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="flex gap-2 mb-5">
              <input data-testid="new-group-input" value={newGroup} onChange={(ev) => setNewGroup(ev.target.value)} onKeyDown={(ev) => { if (ev.key === "Enter") addGroup(); }} placeholder="Nama kelompok baru" className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              <button data-testid="add-group-button" onClick={addGroup} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Tambah</button>
            </div>
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.id} data-testid={`group-row-${g.id}`} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5">
                  <Layers className="h-4 w-4 text-[#F16522]" />
                  <span className="flex-1 text-sm font-medium text-slate-700">{g.name}</span>
                  <button data-testid={`rename-group-${g.id}`} onClick={() => renameGroup(g)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600" title="Ubah nama"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-group-${g.id}`} onClick={() => deleteGroup(g)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Hapus"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Belum ada kelompok.</p>}
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold text-slate-900">Tambah Karyawan</h3>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>

            {created ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-semibold text-emerald-800 mb-2">Akun login karyawan dibuat:</p>
                  <p className="text-sm text-emerald-700 font-mono">Username (Nomor ID): {created.username}</p>
                  <p className="text-sm text-emerald-700 font-mono">Sandi: {created.password}</p>
                </div>
                <button onClick={() => { setCreated(null); }} className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Tambah Lagi</button>
                <button onClick={() => setOpen(false)} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700">Tutup</button>
              </div>
            ) : (
              <form onSubmit={save} className="space-y-4">
                <div className="flex items-center gap-4">
                  {form.photo_url ? (
                    <img src={fileUrl(form.photo_url)} alt="preview" className="h-16 w-16 rounded-xl object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-400"><Upload className="h-6 w-6" /></div>
                  )}
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="employee-photo-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Unggah Foto
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nomor ID *" testid="employee-id-input" value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} required />
                  <Field label="Jabatan" testid="employee-position-input" value={form.position} onChange={(v) => setForm({ ...form, position: v })} />
                </div>
                <Field label="Nama Karyawan *" testid="employee-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <p className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">Akun login otomatis dibuat: <span className="font-semibold text-slate-700">Username = Nomor ID</span>, sandi standar <span className="font-mono font-semibold text-slate-700">mankeu123</span>.</p>
                <button data-testid="save-employee-button" type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="delete-employee-modal">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50">
              <AlertTriangle className="h-7 w-7 text-rose-600" />
            </div>
            <h3 className="mt-4 text-center font-heading text-xl font-extrabold text-slate-900">Hapus Karyawan?</h3>
            <p className="mt-2 text-center text-sm text-slate-500">Anda akan menghapus karyawan berikut secara permanen:</p>
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              {deleteTarget.photo_url ? (
                <img src={fileUrl(deleteTarget.photo_url)} alt="" className="h-11 w-11 rounded-lg object-cover" />
              ) : (
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-base font-bold text-blue-600">{deleteTarget.name[0]}</div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{deleteTarget.name}</p>
                <p className="text-xs font-mono text-slate-500">No. ID: {deleteTarget.employee_id}</p>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-700">
              Data ini <span className="font-bold">permanen dan tidak bisa dikembalikan</span>, termasuk seluruh riwayat kehadiran, hasil kuis, dan nilai karyawan.
            </div>
            <div className="mt-5 flex gap-3">
              <button data-testid="cancel-delete-employee" onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition">
                Batal
              </button>
              <button data-testid="confirm-delete-employee" onClick={confirmDelete} disabled={deleting} className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Hapus Permanen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, testid, required, placeholder }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <input
      data-testid={testid}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
    />
  </div>
);
